import { FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useErrorBoundary } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Checkbox,
	FlexGap,
	GraphQLErrorMessage,
	Label,
	Modal,
	NumericInput,
	Popover,
	Select,
	TextArea,
	tokens,
	useToasts,
} from '@treams/treams-ui';

const REASONS = [
	graphql.SalaryChangeReason.PeriodicStep,
	graphql.SalaryChangeReason.Performance,
	graphql.SalaryChangeReason.Promotion,
	graphql.SalaryChangeReason.MarketCorrection,
	graphql.SalaryChangeReason.CollectiveAgreement,
	graphql.SalaryChangeReason.OneOffBonus,
];

const BASES = [
	graphql.SalaryChangeBasis.Performance,
	graphql.SalaryChangeBasis.Skills,
	graphql.SalaryChangeBasis.Responsibility,
	graphql.SalaryChangeBasis.MarketPosition,
];

export default function CreateSalaryChangeModal(props: {
	userID: string;
	onClose: () => void;
	onCreated: () => void;
}) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [error, attempt] = useErrorBoundary();
	const [create, { loading }] = graphql.useCreateSalaryChangeMutation();

	const formik = useFormik({
		initialValues: {
			amount: '',
			fteHours: '',
			reason: '' as graphql.SalaryChangeReason | '',
			bases: [] as graphql.SalaryChangeBasis[],
			explanation: '',
		},
		onSubmit: attempt(async (values) => {
			const amount = Number(values.amount);
			const fteHours = values.fteHours === '' ? undefined : Number(values.fteHours);
			if (!values.reason || !Number.isInteger(amount)) {
				return;
			}

			if (fteHours != null && !Number.isInteger(fteHours)) {
				return;
			}

			await create({
				variables: {
					input: {
						userID: props.userID,
						amount,
						fteHours,
						reason: values.reason,
						bases: values.bases,
						explanation: values.explanation.trim() || null,
					},
				},
			});

			addToast(t('salaryDevelopment.saved'), { appearance: 'success', autoDismiss: true });
			props.onCreated();
			props.onClose();
		}),
	});

	const amount = Number(formik.values.amount);
	const fteHours = formik.values.fteHours === '' ? undefined : Number(formik.values.fteHours);
	const fteHoursValid =
		fteHours == null || (Number.isInteger(fteHours) && fteHours >= 1 && fteHours <= 80);
	const canSave =
		Number.isInteger(amount) &&
		amount >= 1 &&
		fteHoursValid &&
		formik.values.reason !== '' &&
		formik.values.bases.length > 0 &&
		!formik.isSubmitting &&
		!loading;

	return (
		<Popover center visible onDismiss={props.onClose}>
			<Modal>
				<Modal.Header title={t('salaryDevelopment.add')} onClose={props.onClose} />
				<FormikProvider value={formik}>
					<Modal.Contents>
						<FlexGap flexDirection="column" gap={tokens.gap.group}>
							<FlexGap gap={tokens.gap.list}>
								<NumericInput
									label={t('salaryDevelopment.form.amount')}
									prefix="€"
									value={formik.values.amount}
									onChange={(value) => formik.setFieldValue('amount', value ?? '')}
								/>
								<NumericInput
									label={t('salaryDevelopment.form.fteHours')}
									value={formik.values.fteHours}
									onChange={(value) => formik.setFieldValue('fteHours', value ?? '')}
								/>
							</FlexGap>
							<Box>
								<Label mb={2}>{t('salaryDevelopment.form.reason')}</Label>
								<Select
									search={false}
									placeholder={t('salaryDevelopment.form.reasonPlaceholder')}
									selectedValue={formik.values.reason || undefined}
									options={REASONS.map((reason) => ({
										value: reason,
										title: t(`salaryDevelopment.reason.${reason}`),
									}))}
									onChange={(value) => formik.setFieldValue('reason', value ?? '')}
								/>
							</Box>
							<Box>
								<Label mb={1}>{t('salaryDevelopment.form.bases')}</Label>
								<FlexGap flexDirection="column" gap={tokens.gap.inline}>
									{BASES.map((basis) => (
										<Checkbox
											key={basis}
											checked={formik.values.bases.includes(basis)}
											label={t(`salaryDevelopment.basis.${basis}`)}
											onClick={() => {
												const selected = formik.values.bases.includes(basis)
													? formik.values.bases.filter((item) => item !== basis)
													: [...formik.values.bases, basis];
												formik.setFieldValue('bases', selected);
											}}
										/>
									))}
								</FlexGap>
							</Box>
							<Box>
								<Label mb={2}>{t('salaryDevelopment.form.explanation')}</Label>
								<TextArea
									minRows={3}
									placeholder={t('salaryDevelopment.form.explanationPlaceholder')}
									value={formik.values.explanation}
									onChange={(text) => formik.setFieldValue('explanation', text)}
								/>
							</Box>
							<GraphQLErrorMessage error={error} />
						</FlexGap>
					</Modal.Contents>
					<Modal.FormFooter
						disabled={!canSave}
						saveTitle={t('common.save')}
						onCancel={props.onClose}
						onSave={() => formik.submitForm()}
					/>
				</FormikProvider>
			</Modal>
		</Popover>
	);
}
